import parse from 'html-react-parser';
import { useState, useEffect } from 'react';

import Accordion from '~components/Accordion/Accordion';
import AccordionItem from '~components/Accordion/AccordionItem';
import { Phone } from '~components/AllianceCallService/AllianceCallService';
import { usePageContext } from '~components/PageDataProvider/PageDataProvider';
import Grid from '~components/_layout/Grid/Grid';
import Section from '~components/_layout/Section';
import { addUrlParam, getPpccParamValueByNameAndPage } from '~globals/common';
import useAllianceData from '~hooks/useAllianceData';
import { useAppSelector } from '~hooks/useRedux';
import useWindowSize from '~hooks/useWindowSize';

const FAQRedesign = () => {
    const pageData = usePageContext();
    const pageCode = pageData.ppccPageCode || '';

    const allid = useAppSelector((state) => state.global.allianceId);
    const personalCodeData = useAppSelector((state) => state.display.personalCode);
    const zipcode = useAppSelector((state) => state.global.geoLocation.zip);

    const [allianceResponse, status] = useAllianceData(allid);
    const [ehmpUrl, setEhmpUrl] = useState(`${process.env.GATSBY_EHMP_URL}ehi/medicare/fast-quote`);
    const { isDesktop } = useWindowSize();

    const { redirectParams } = personalCodeData;
    const planType = redirectParams.type;
    const ehmpTarget = redirectParams.target;
    const partnerDisplayName =
        allianceResponse?.partnerAllianceInfo?.allianceData?.partnerDisplayName;

    useEffect(() => {
        let url = ehmpUrl;

        url = addUrlParam(url, 'allid', allid);
        url = addUrlParam(url, 'type', planType);
        url = addUrlParam(url, 'target', ehmpTarget);

        if (zipcode) {
            url = addUrlParam(url, 'zip', zipcode);
        }

        setEhmpUrl(url);
    });

    const defaultAccordions = [
        {
            id: 1,
            title: `Is eHealth able to identify me by my PlanMatch code?`,
            content: (
                <>
                    <p className="text-sm md:text-lg mb-4">
                        No, your PlanMatch Code does not include any personally identifiable
                        information like name, address, age, or gender. Only your doctor, pharmacy,
                        and prescription information are securely obtained{' '}
                        {partnerDisplayName ? 'from ' + partnerDisplayName : ''}. Your PlanMatch
                        code is only valid for 12 months, after that the code expires and your
                        doctor, pharmacy, and prescription information are deleted.
                    </p>
                </>
            ),
            dataWaLink: 'Accordion: planmatch code',
        },
        {
            id: 2,
            title: 'What if my doctor or prescription information is incomplete or incorrect?',
            content: (
                <>
                    <p className="text-sm md:text-lg mb-4">
                        It is possible that your health information has not yet been{' '}
                        {partnerDisplayName
                            ? 'updated within ' +
                              partnerDisplayName +
                              ', or you may go to another provider and get prescriptions that aren’t tracked via ' +
                              partnerDisplayName
                            : 'fully updated with your provider or pharmacy'}
                        . eHealth recommends that when you review the information shown after you
                        enter your PlanMatch Code, immediately confirm your doctor and prescription
                        information. You may add, change, or delete any incorrect or incomplete
                        information on the plan recommendation page.
                    </p>
                </>
            ),
            dataWaLink: 'Accordion: incorrect or incomplete information',
        },
        {
            id: 3,
            title: 'Why is my PlanMatch Code not working?',
            content: (
                <>
                    <p className="text-sm md:text-lg mb-4">
                        There can be a couple of reasons the PlanMatch does not work. Please double
                        check the 6-digit code that was displayed in the email or letter you
                        received, and confirm you entered it correctly on the site. If that does not
                        work, there is most likely a technical issue. You may call eHealth at{' '}
                        <Phone classname="font-bold">TTY users 711</Phone> to alert a licensed
                        insurance agent. You may still shop and compare Medicare plans in your area
                        without your PlanMatch Code.{' '}
                        <a href={ehmpUrl} className="underline text-primary">
                            Click here
                        </a>{' '}
                        to get started.
                    </p>
                </>
            ),
            dataWaLink: 'Accordion: code not working',
        },
    ];

    if (status === 'loading') {
        return null;
    }

    if (status === 'error') {
        console.log('allID loading error');
    }

    if (!allianceResponse.partnerAllianceInfo) {
        console.log('allID did not return partner data');
    }

    const pageConfigs = allianceResponse.partnerAllianceInfo?.landingPageConfigs;

    const customAccordions: typeof defaultAccordions = [];

    if (pageConfigs) {
        ['1', '2', '3', '4', '5'].forEach((n, i) => {
            const sectionHeader = getPpccParamValueByNameAndPage({
                pageCode,
                pageConfigs,
                paramName: `faq_section_${n}_header`,
            }) as string;
            const sectionParagraph = getPpccParamValueByNameAndPage({
                pageCode,
                pageConfigs,
                paramName: `faq_section_${n}_paragraph`,
            }) as string;
            if (!(sectionHeader && sectionParagraph)) {
                return;
            }
            const rawContent: string = sectionParagraph?.replaceAll(
                '{{clickHere}}',
                `<a href="${ehmpUrl}" className="text-primary font-bold">Click here</a>`
            );
            let parsedContent;
            if (sectionParagraph.includes('{{TFN}}')) {
                const splitContent = rawContent.split('{{TFN}}');
                parsedContent = splitContent.map((chunk, i) => {
                    if (i < splitContent.length - 1) {
                        return (
                            <span key={chunk}>
                                {parse(chunk)}
                                <Phone islink={!isDesktop} classname="font-bold">
                                    TTY users 711
                                </Phone>
                            </span>
                        );
                    } else return parse(chunk);
                });
            } else {
                parsedContent = parse(rawContent);
            }
            customAccordions[i] = {
                id: Number(n),
                title: sectionHeader,
                content: <div className="text-sm md:text-lg mb-4">{parsedContent}</div>,
                dataWaLink: sectionHeader,
            };
        });
    }

    return (
        <Section extraClasses="faq w-full py-6 md:py-10 lg:py-16 text-black">
            <Grid>
                <div className="text-center col-span-4 md:col-span-8 lg:col-span-12">
                    <h2 className="font-bold text-xl md:text-2xl lg:text-3xl mb-4 md:mb-0">
                        Frequently Asked Questions
                    </h2>
                </div>
                <div className="col-span-4 md:col-span-8 lg:col-span-10 lg:col-start-2">
                    <Accordion>
                        {(customAccordions.length ? customAccordions : defaultAccordions).map(
                            (accordion, index) => {
                                return (
                                    <AccordionItem
                                        key={index}
                                        id={`pc-faq-${accordion.id}`}
                                        title={
                                            <span className="font-semibold text-base md:text-lg lg:text-xl py-2 md:py-3 lg:py-5 md:mr-4">
                                                {accordion.title}
                                            </span>
                                        }
                                        data-wa-link={accordion.dataWaLink}
                                    >
                                        <div className="text-gray-accessible">
                                            {accordion.content}
                                        </div>
                                    </AccordionItem>
                                );
                            }
                        )}
                    </Accordion>
                </div>
            </Grid>
        </Section>
    );
};

export default FAQRedesign;
